/**
 * Camunda 7.18 Authorization REST API Service
 * https://docs.camunda.org/manual/7.18/reference/rest/authorization/
 */

import { CamundaApiClient, getCamundaApiClient } from '../CamundaApiClient';
import type {
  Authorization,
  AuthorizationQuery,
  AuthorizationCreateDto,
  AuthorizationUpdateDto,
  AuthorizationCheckResult,
  CountResult,
} from '../types';

export class AuthorizationService {
  private client: CamundaApiClient;
  private basePath = '/engine-rest/authorization';

  constructor(client?: CamundaApiClient) {
    this.client = client || getCamundaApiClient();
  }

  // ============================================================================
  // Get List
  // ============================================================================

  /**
   * Get authorizations
   */
  async getList(query?: AuthorizationQuery): Promise<Authorization[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<Authorization[]>(`${this.basePath}${params}`);
  }

  /**
   * Get authorizations count
   */
  async getCount(query?: AuthorizationQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/count${params}`);
  }

  // ============================================================================
  // Get Single
  // ============================================================================

  /**
   * Get authorization by ID
   */
  async getById(id: string): Promise<Authorization> {
    return this.client.get<Authorization>(`${this.basePath}/${id}`);
  }

  // ============================================================================
  // Create
  // ============================================================================

  /**
   * Create a new authorization
   */
  async create(dto: AuthorizationCreateDto): Promise<Authorization> {
    return this.client.post<Authorization>(`${this.basePath}/create`, dto);
  }

  // ============================================================================
  // Update
  // ============================================================================

  /**
   * Update an authorization
   */
  async update(id: string, dto: AuthorizationUpdateDto): Promise<void> {
    await this.client.put<void>(`${this.basePath}/${id}`, dto);
  }

  // ============================================================================
  // Delete
  // ============================================================================

  /**
   * Delete an authorization
   */
  async delete(id: string): Promise<void> {
    await this.client.delete<void>(`${this.basePath}/${id}`);
  }

  // ============================================================================
  // Check
  // ============================================================================

  /**
   * Check authorization
   */
  async check(options: {
    permissionName: string;
    resourceName: string;
    resourceType: number;
    resourceId?: string;
    userId?: string;
  }): Promise<AuthorizationCheckResult> {
    const params = new URLSearchParams();
    params.append('permissionName', options.permissionName);
    params.append('resourceName', options.resourceName);
    params.append('resourceType', String(options.resourceType));
    if (options.resourceId) params.append('resourceId', options.resourceId);
    if (options.userId) params.append('userId', options.userId);
    return this.client.get<AuthorizationCheckResult>(`${this.basePath}/check?${params.toString()}`);
  }

  // ============================================================================
  // Available Operations
  // ============================================================================

  /**
   * Get available operations
   */
  async getAvailableOperations(): Promise<{ links: { href: string; method: string; rel: string }[] }> {
    return this.client.get<{ links: { href: string; method: string; rel: string }[] }>(`${this.basePath}`);
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private buildQueryParams(query?: AuthorizationQuery): string {
    if (!query) return '';
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else {
          params.append(key, String(value));
        }
      }
    });
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }
}

// Singleton instance
let authorizationServiceInstance: AuthorizationService | null = null;

export function getAuthorizationService(): AuthorizationService {
  if (!authorizationServiceInstance) {
    authorizationServiceInstance = new AuthorizationService();
  }
  return authorizationServiceInstance;
}

export default AuthorizationService;
